import type {
  KeyResult,
  KeyResultStatus,
  KeyResultDto,
  Objective,
  ObjectiveCategory,
  ObjectiveStatus,
  OkrOwnerUser,
  OkrOwnerUserDto,
  PaginatedResponse,
  ObjectiveDto,
  EntityResponse,
  UserStatus,
} from "@/types";

/** BE may return `{ data: entity }` for single-resource endpoints. */
function unwrapEntityRow<T>(raw: T | EntityResponse<T>): T {
  if (raw && typeof raw === "object" && "data" in raw) {
    return (raw as EntityResponse<T>).data;
  }
  return raw as T;
}

function toFiniteNumber(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(",", "."));
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

export function parseOkrOwner(u: OkrOwnerUserDto): OkrOwnerUser {
  return {
    pid: String(u.pid ?? ""),
    email: u.email == null ? undefined : String(u.email),
    fullName: String(u.fullName ?? ""),
    avatarUrl: u.avatarUrl == null ? undefined : String(u.avatarUrl),
    position: u.position == null ? undefined : String(u.position),
    status: u.status == null ? undefined : (u.status as UserStatus),
    createdAt: u.createdAt == null ? undefined : String(u.createdAt),
    updatedAt: u.updatedAt == null ? undefined : String(u.updatedAt),
    isLeader: typeof u.isLeader === "boolean" ? u.isLeader : undefined,
  };
}

export function parseKeyResult(raw: KeyResultDto | EntityResponse<KeyResultDto>): KeyResult {
  const k = unwrapEntityRow<KeyResultDto>(raw);
  return {
    pid: String(k.pid ?? ""),
    code: String(k.code ?? ""),
    name: String(k.name ?? ""),
    description: k.description == null ? undefined : String(k.description),
    context: k.context == null ? undefined : String(k.context),
    status: (k.status ?? "active") as KeyResultStatus,
    progress: toFiniteNumber(k.progress, 0),
    targetProgress: toFiniteNumber(k.targetProgress, 100),
    weight: k.weight == null ? undefined : toFiniteNumber(k.weight, 0),
    startDate: k.startDate == null ? undefined : String(k.startDate),
    dueDate: k.dueDate == null ? undefined : String(k.dueDate),
    orderIndex: typeof k.orderIndex === "number" ? k.orderIndex : undefined,
    objectivePid: k.objectivePid ? String(k.objectivePid) : k.objective?.pid ? String(k.objective.pid) : undefined,
    objective: k.objective
      ? {
          pid: String(k.objective.pid ?? ""),
          code: String(k.objective.code ?? ""),
          name: String(k.objective.name ?? ""),
        }
      : undefined,
    owner: k.owner ? parseOkrOwner(k.owner) : undefined,
    contributors: Array.isArray(k.contributors) ? k.contributors : [],
    contributorCount:
      typeof k.contributorCount === "number" ? k.contributorCount : Array.isArray(k.contributors) ? k.contributors.length : 0,
    createdAt: String(k.createdAt ?? ""),
    updatedAt: String(k.updatedAt ?? ""),
  };
}

export function parseObjective(raw: ObjectiveDto | EntityResponse<ObjectiveDto>): Objective {
  const o = unwrapEntityRow<ObjectiveDto>(raw);
  return {
    pid: String(o.pid ?? ""),
    code: String(o.code ?? ""),
    name: String(o.name ?? ""),
    description: o.description == null ? undefined : String(o.description),
    context: o.context == null ? undefined : String(o.context),
    status: (o.status ?? "active") as ObjectiveStatus,
    // Pre-migration rows have no category; "company" is the neutral lane.
    category: (o.category ?? "company") as ObjectiveCategory,
    progress: toFiniteNumber(o.progress, 0),
    targetProgress: toFiniteNumber(o.targetProgress, 100),
    startDate: o.startDate == null ? undefined : String(o.startDate),
    dueDate: o.dueDate == null ? undefined : String(o.dueDate),
    orderIndex: typeof o.orderIndex === "number" ? o.orderIndex : undefined,
    cycle: o.cycle as Objective["cycle"],
    owner: o.owner ? parseOkrOwner(o.owner) : undefined,
    keyResults: Array.isArray(o.keyResults)
      ? o.keyResults.map(parseKeyResult)
      : undefined,
    createdAt: String(o.createdAt ?? ""),
    updatedAt: String(o.updatedAt ?? ""),
  };
}

export function parseObjectivePage(res: { data?: ObjectiveDto[]; total?: number }): PaginatedResponse<Objective> {
  const r = res;
  const rows = r.data ?? [];
  return {
    data: rows.map(parseObjective),
    total: typeof r.total === "number" ? r.total : rows.length,
  };
}

export function parseKeyResultPage(res: { data?: KeyResultDto[]; total?: number }): PaginatedResponse<KeyResult> {
  const r = res;
  const rows = r.data ?? [];
  return {
    data: rows.map(parseKeyResult),
    total: typeof r.total === "number" ? r.total : rows.length,
  };
}
